import React from "react";
import { useLongPress } from "@/components/LongPressMenu";

/**
 * useSelection — multi-row selection model shared by Bible verses and
 * Catechism paragraphs.
 *
 * Interaction rules (matches the product spec):
 *   • Long-press starts (or extends) the selection. If the selection
 *     already has MORE THAN ONE item, the long-press resets it to just
 *     the pressed row — the user is "re-anchoring" their selection.
 *   • In selection mode (size > 0), a short click toggles a row.
 *   • A double-click clears the entire selection.
 *
 * The hook is intentionally agnostic of the data shape; rows are
 * identified by an opaque string key.
 */
export function useSelection() {
    const [selectedKeys, setSelectedKeys] = React.useState(() => new Set());
    const inSelectionMode = selectedKeys.size > 0;

    const startOrReset = React.useCallback((key) => {
        setSelectedKeys((prev) => {
            if (prev.size > 1) return new Set([key]);
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    }, []);

    const toggle = React.useCallback((key) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const clear = React.useCallback(() => {
        setSelectedKeys((prev) => (prev.size === 0 ? prev : new Set()));
    }, []);

    return { selectedKeys, inSelectionMode, startOrReset, toggle, clear };
}

const SINGLE_CLICK_DELAY_MS = 230;

/**
 * useRowGestures — wraps `useLongPress` with click + dblclick handlers
 * that only fire in selection mode. The browser always dispatches a
 * click before dblclick, so we debounce single-clicks by the typical
 * dblclick window (≈230 ms) and cancel the pending click when a real
 * dblclick comes in.
 *
 * When `inSelectionMode` is false, click/dblclick are no-ops so the
 * normal text-reading experience is preserved (taps don't toggle
 * selection on a fresh page).
 */
export function useRowGestures({ inSelectionMode, onLongPress, onShortClick, onDoubleClick }) {
    const longPress = useLongPress(onLongPress);
    const clickTimer = React.useRef(null);

    const cancelPendingClick = React.useCallback(() => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
    }, []);

    React.useEffect(() => cancelPendingClick, [cancelPendingClick]);

    const onClick = React.useCallback((e) => {
        if (!inSelectionMode) return;
        // The `useLongPress` `onPointerUp` will have already
        // `stopPropagation`'d when its timer fired — so this click
        // handler only sees genuine taps, not a release-after-hold.
        e.stopPropagation();
        if (clickTimer.current) return; // dedup rapid duplicate clicks
        clickTimer.current = setTimeout(() => {
            clickTimer.current = null;
            onShortClick();
        }, SINGLE_CLICK_DELAY_MS);
    }, [inSelectionMode, onShortClick]);

    const onDoubleClickHandler = React.useCallback((e) => {
        if (!inSelectionMode) return;
        e.stopPropagation();
        cancelPendingClick();
        onDoubleClick();
    }, [inSelectionMode, onDoubleClick, cancelPendingClick]);

    return {
        ...longPress,
        onClick,
        onDoubleClick: onDoubleClickHandler,
    };
}
